import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { CATEGORIES, CITIES } from "../lib/data";
import { useToast } from "../hooks/use-toast";

export default function RequirementsForm({ variant = "home" }) {
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [budget, setBudget] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (!category) { toast({ title: "Please pick a category", variant: "destructive" }); return; }
    if (description.trim().length < 10) { toast({ title: "Add a bit more detail (min 10 chars)", variant: "destructive" }); return; }
    setSubmitting(true);
    try {
      await api.post("/requirements", { category, description, name, phone, city, budget });
      toast({ title: "Requirement posted", description: "Sellers matching your need will reach out shortly." });
      setDescription(""); setName(""); setPhone(""); setBudget("");
    } catch (err) {
      toast({ title: "Could not post", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const compact = variant === "compact";

  return (
    <form onSubmit={submit} className={compact ? "space-y-3" : "grid grid-cols-1 md:grid-cols-2 gap-3"} data-testid="requirement-form">
      <div>
        <label className="text-xs font-semibold text-ink">Category *</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-neutral-200 bg-white text-sm focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none" data-testid="req-category" required>
          <option value="">Select category…</option>
          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-semibold text-ink">City</label>
        <select value={city} onChange={(e) => setCity(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-neutral-200 bg-white text-sm focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none" data-testid="req-city">
          <option value="">Any city</option>
          {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <label className="text-xs font-semibold text-ink">Describe what you're looking for *</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={compact ? 2 : 3} maxLength={1000} placeholder="e.g. Looking for a 3BHK apartment on rent in Sector 45, Gurgaon under ₹35k" className="mt-1 w-full px-3 py-2 rounded-lg border border-neutral-200 bg-white text-sm focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none" data-testid="req-description" required />
        <p className="text-[10px] text-muted-foreground mt-1">{description.length}/1000</p>
      </div>
      <div>
        <label className="text-xs font-semibold text-ink">Your name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" className="mt-1 w-full h-10 px-3 rounded-lg border border-neutral-200 bg-white text-sm" data-testid="req-name" />
      </div>
      <div>
        <label className="text-xs font-semibold text-ink">Phone</label>
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Optional (will be masked)" className="mt-1 w-full h-10 px-3 rounded-lg border border-neutral-200 bg-white text-sm" data-testid="req-phone" />
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <label className="text-xs font-semibold text-ink">Budget</label>
        <input value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="e.g. ₹30k–₹40k / mo" className="mt-1 w-full h-10 px-3 rounded-lg border border-neutral-200 bg-white text-sm" data-testid="req-budget" />
      </div>
      <div className={compact ? "" : "md:col-span-2 flex items-center gap-2"}>
        <button type="submit" disabled={submitting} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="req-submit">
          <Send className="h-4 w-4" /> {submitting ? "Posting…" : "Post Requirement"}
        </button>
      </div>
    </form>
  );
}
